import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Button from '../UI/Button';

const TicketContainer = styled.section`
  padding: 6rem 0;
  background: ${({ theme }) => theme.colors.surface};
  position: relative;
`;

const TicketContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;

const TicketInfo = styled.div`
  z-index: 2;
`;

const TicketTitle = styled(motion.h2)`
  font-size: 3rem;
  margin-bottom: 2rem;
  background: ${({ theme }) => theme.gradients.primary};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.5rem;
  }
`;

const TicketDescription = styled(motion.p)`
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 2rem;
  font-size: 1.1rem;
  line-height: 1.7;
`;

const TicketFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const FeatureItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 10px;
  border-left: 4px solid ${({ theme }) => theme.colors.primary.yellow};
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 229, 0, 0.1);
    transform: translateX(10px);
  }
`;

const FeatureIcon = styled.div`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.primary.yellow};
  min-width: 40px;
`;

const FeatureContent = styled.div`
  h3 {
    color: ${({ theme }) => theme.colors.primary.yellow};
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    font-weight: 600;
  }
  
  p {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 0.95rem;
  }
`;

const TicketCard = styled(motion.div)`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.background.secondary}, ${({ theme }) => theme.colors.surface});
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 20px;
  padding: 2rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 8px;
    background: ${({ theme }) => theme.gradients.primary};
  }
`;

const TicketHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  position: relative;
  z-index: 2;
`;

const TicketType = styled.div`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.primary.yellow};
  margin-bottom: 0.5rem;
  font-weight: 700;
`;

const TicketSubtitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1rem;
`;

const PriceSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  position: relative;
  z-index: 2;
`;

const PriceAmount = styled.div`
  font-size: 3rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary.blue};
  font-family: ${({ theme }) => theme.fonts.mono};
  text-shadow: 0 0 20px rgba(0, 128, 214, 0.5);
`;

const PriceNote = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const IncludesSection = styled.div`
  margin-bottom: 2rem;
  position: relative;
  z-index: 2;
`;

const IncludesTitle = styled.h4`
  color: ${({ theme }) => theme.colors.primary.yellow};
  margin-bottom: 1rem;
  font-size: 1.1rem;
  font-weight: 600;
`;

const IncludesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const IncludesItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.9rem;
  
  &::before {
    content: '✓';
    color: ${({ theme }) => theme.colors.primary.yellow};
    font-weight: bold;
    min-width: 16px;
  }
`;

const EventInfo = styled.div`
  background: rgba(0, 128, 214, 0.1);
  padding: 1rem;
  border-radius: 10px;
  border-left: 4px solid ${({ theme }) => theme.colors.primary.blue};
  text-align: center;
  margin-top: 2rem;
  position: relative;
  z-index: 2;
`;

const EventDate = styled.div`
  color: ${({ theme }) => theme.colors.primary.yellow};
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
`;

const EventLocation = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.9rem;
`;

const TicketBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(155, 89, 182, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(155, 89, 182, 0.05) 1px, transparent 1px);
  background-size: 20px 20px;
  opacity: 0.5;
  animation: ticketMove 15s linear infinite;
  
  @keyframes ticketMove {
    0% { transform: translate(0, 0); }
    100% { transform: translate(20px, 20px); }
  }
`;

const TicketSection = () => {
  const features = [
    {
      icon: '🎯',
      title: 'Full Access',
      description: 'Complete access to all sessions, workshops, and networking events'
    },
    {
      icon: '🍽️',
      title: 'Meals Included',
      description: 'Breakfast, lunch, and refreshments provided both days'
    },
    {
      icon: '🎁',
      title: 'Swag Bag',
      description: 'Exclusive Security BSides Albuquerque merchandise and sponsor goodies'
    },
    {
      icon: '📜',
      title: 'Certificate',
      description: 'Digital certificate of attendance for professional development'
    }
  ];

  const includes = [
    '2-day conference access',
    'All keynote sessions',
    'Technical workshops',
    'Networking events',
    'Breakfast & lunch both days',
    'Coffee & refreshments',
    'BSides swag bag',
    'Digital certificate',
    'Access to speaker slides',
    'Community Discord access'
  ];

  return (
    <TicketContainer id="ticket">
      <div className="container">
        <TicketContent>
          <TicketInfo>
            <TicketTitle
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              Conference Pass
            </TicketTitle>
            
            <TicketDescription
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Secure your spot at Security BSides Albuquerque 2025 and join the cybersecurity 
              community for two days of learning, networking, and innovation.
            </TicketDescription>
            
            <TicketFeatures>
              {features.map((feature, index) => (
                <FeatureItem
                  key={feature.title}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.02 }}
                >
                  <FeatureIcon>{feature.icon}</FeatureIcon>
                  <FeatureContent>
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </FeatureContent>
                </FeatureItem>
              ))}
            </TicketFeatures>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              viewport={{ once: true }}
              style={{ textAlign: 'center' }}
            >
              <Button variant="primary" size="large" href="https://www.eventbrite.com/e/security-bsides-albuquerque-cybersecurity-conference-tickets-1244346185939">
                🎫 Register Now
              </Button>
            </motion.div>
          </TicketInfo>
          
          <TicketCard
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.02 }}
          >
            <TicketBackground />
            
            <TicketHeader>
              <TicketType>BSides ABQ 2025</TicketType>
              <TicketSubtitle>Conference Pass</TicketSubtitle>
            </TicketHeader>
            
            <PriceSection>
              <PriceAmount>$0-$23.18</PriceAmount>
              <PriceNote>Early Bird Special</PriceNote>
            </PriceSection>
            
            <IncludesSection>
              <IncludesTitle>What's Included:</IncludesTitle>
              <IncludesList>
                {includes.map((item, index) => (
                  <IncludesItem key={index}>{item}</IncludesItem>
                ))}
              </IncludesList>
            </IncludesSection>
            
            <EventInfo>
              <EventDate>📅 July 25-26, 2025</EventDate>
              <EventLocation>Albuquerque, New Mexico</EventLocation>
            </EventInfo>
          </TicketCard>
        </TicketContent>
      </div>
    </TicketContainer>
  );
};

export default TicketSection;